import { useCallback, useLayoutEffect, useState, type RefObject } from 'react';

/** Separación entre el trigger y el popover. */
const ANCHOR_GAP = 4;
/** Margen mínimo que se respeta contra los bordes del viewport. */
const VIEWPORT_MARGIN = 8;
/** Alto máximo deseable del popover cuando hay espacio de sobra. */
const PREFERRED_MAX_HEIGHT = 280;
/** Alto mínimo utilizable; por debajo de esto conviene abrir hacia arriba. */
const MIN_USABLE_HEIGHT = 160;
/** Ancho mínimo del popover, salvo que el viewport sea más angosto (móvil). */
const MIN_POPOVER_WIDTH = 240;

export interface AnchorRect {
  top: number;
  left: number;
  bottom: number;
  width: number;
}

export interface ViewportSize {
  width: number;
  height: number;
}

export interface DropdownPosition {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
}

function clamp(value: number, minValue: number, maxValue: number): number {
  if (maxValue < minValue) return minValue;
  return Math.min(Math.max(value, minValue), maxValue);
}

/**
 * Calcula la posición fija de un popover anclado a su trigger, manteniéndolo
 * siempre dentro del viewport: se voltea hacia arriba si abajo no cabe y su
 * alto se recorta al espacio disponible en vez de desbordarse.
 */
export function computeDropdownPosition(
  anchor: AnchorRect,
  viewport: ViewportSize,
  minWidth: number = MIN_POPOVER_WIDTH,
): DropdownPosition {
  const availableWidth = viewport.width - VIEWPORT_MARGIN * 2;
  const width = Math.min(Math.max(anchor.width, minWidth), availableWidth);
  const left = clamp(anchor.left, VIEWPORT_MARGIN, viewport.width - VIEWPORT_MARGIN - width);

  const spaceBelow = viewport.height - anchor.bottom - ANCHOR_GAP - VIEWPORT_MARGIN;
  const spaceAbove = anchor.top - ANCHOR_GAP - VIEWPORT_MARGIN;
  const shouldOpenBelow = spaceBelow >= MIN_USABLE_HEIGHT || spaceBelow >= spaceAbove;

  if (shouldOpenBelow) {
    return {
      top: anchor.bottom + ANCHOR_GAP,
      left,
      width,
      maxHeight: Math.max(clamp(spaceBelow, 0, PREFERRED_MAX_HEIGHT), 0),
    };
  }

  const maxHeight = Math.max(clamp(spaceAbove, 0, PREFERRED_MAX_HEIGHT), 0);
  return { top: anchor.top - ANCHOR_GAP - maxHeight, left, width, maxHeight };
}

/**
 * Mantiene sincronizada la posición de un popover en portal con su trigger,
 * recalculándola al abrir y ante scroll o resize.
 */
export function useDropdownPosition(
  anchorRef: RefObject<HTMLElement | null>,
  isOpen: boolean,
): DropdownPosition | null {
  const [position, setPosition] = useState<DropdownPosition | null>(null);

  const recalculate = useCallback(() => {
    const anchorElement = anchorRef.current;
    if (!anchorElement) return;
    const rect = anchorElement.getBoundingClientRect();
    setPosition(
      computeDropdownPosition(
        { top: rect.top, left: rect.left, bottom: rect.bottom, width: rect.width },
        { width: window.innerWidth, height: window.innerHeight },
      ),
    );
  }, [anchorRef]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return;
    }
    recalculate();
    window.addEventListener('resize', recalculate);
    window.addEventListener('scroll', recalculate, true);
    return () => {
      window.removeEventListener('resize', recalculate);
      window.removeEventListener('scroll', recalculate, true);
    };
  }, [isOpen, recalculate]);

  return position;
}
